/**
 * Date/time helpers for Canada Post pickups.
 *
 * Pickup dates are bare YYYY-MM-DD strings — Canada Post has no notion of a
 * timezone here, and `new Date('2026-08-25')` parses as UTC midnight, which is
 * the previous day in Montreal. Everything below builds and reads those strings
 * in local time so a pickup booked for Tuesday never renders as Monday.
 */

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const toDateStr = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/** Parses YYYY-MM-DD as a local date, not a UTC instant. */
export const parseDateStr = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

export const todayStr = (): string => toDateStr(new Date());

/**
 * Days Canada Post does not collect — a copy of
 * packages/nestjs/src/canada-post/canada-post-holidays.ts (keep the two in step).
 *
 * Canada Post refuses a pickup on one of these with "Pickup is not available for
 * the Postal Code you selected" (Lakeshore, Sep 30, 2026), so the strip must never
 * offer — let alone pre-select — one.
 */
const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (year: number, month: number, day: number) => `${year}-${pad(month)}-${pad(day)}`;

/** Easter Sunday (anonymous Gregorian algorithm), as [month, day]. */
const easterSunday = (year: number): [number, number] => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return [month, day];
};

/** The nth (1-based) given weekday of a month. */
const nthWeekday = (year: number, month: number, weekday: number, n: number): string => {
  const first = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  return ymd(year, month, 1 + ((weekday - first + 7) % 7) + (n - 1) * 7);
};

const shiftDays = (year: number, month: number, day: number, delta: number): string => {
  const date = new Date(Date.UTC(year, month - 1, day + delta));
  return ymd(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
};

const isQuebec = (province?: string | null): boolean =>
  /^(qc|qu[eé]bec)$/i.test((province || '').trim());

const isWeekend = (date: string): boolean => {
  const weekday = new Date(`${date}T12:00:00Z`).getUTCDay();
  return weekday === 0 || weekday === 6;
};

/** Holiday date → name for one year, observed days included. */
const holidaysFor = (year: number, province?: string | null): Map<string, string> => {
  const holidays = new Map<string, string>();
  const [easterMonth, easterDay] = easterSunday(year);

  holidays.set(shiftDays(year, easterMonth, easterDay, -2), 'Good Friday');
  holidays.set(shiftDays(year, easterMonth, easterDay, 1), 'Easter Monday');
  // Victoria Day (Journée nationale des patriotes in Quebec): the Monday on or before May 24.
  const may24 = new Date(Date.UTC(year, 4, 24)).getUTCDay();
  holidays.set(ymd(year, 5, 24 - ((may24 + 6) % 7)), 'Victoria Day');
  holidays.set(nthWeekday(year, 8, 1, 1), 'Civic Holiday');
  holidays.set(nthWeekday(year, 9, 1, 1), 'Labour Day');
  holidays.set(nthWeekday(year, 10, 1, 2), 'Thanksgiving');

  const fixed: [number, number, string][] = [
    [1, 1, "New Year's Day"],
    [7, 1, 'Canada Day'],
    [9, 30, 'National Day for Truth and Reconciliation'],
    [11, 11, 'Remembrance Day'],
    [12, 25, 'Christmas Day'],
    [12, 26, 'Boxing Day'],
  ];
  if (isQuebec(province)) fixed.push([6, 24, 'Fête nationale du Québec']);

  // A fixed-date holiday on a weekend is observed on the next free weekday
  // (Christmas on a Saturday moves to Monday, and Boxing Day then to Tuesday).
  for (const [month, day, name] of fixed) {
    holidays.set(ymd(year, month, day), name);
    if (!isWeekend(ymd(year, month, day))) continue;
    for (let delta = 1; ; delta++) {
      const observed = shiftDays(year, month, day, delta);
      if (!isWeekend(observed) && !holidays.has(observed)) {
        holidays.set(observed, `${name} (observed)`);
        break;
      }
    }
  }
  return holidays;
};

/**
 * The holiday's name when Canada Post does not collect on `date` (YYYY-MM-DD),
 * otherwise null. `province` is the pickup address's province as typed ("QC", "Quebec").
 */
export const canadaPostHoliday = (date: string, province?: string | null): string | null => {
  const year = Number(date.slice(0, 4));
  if (!year) return null;
  return holidaysFor(year, province).get(date) ?? null;
};

/**
 * The next `count` weekdays Canada Post collects on, starting tomorrow.
 * `province` is the pickup address's, for the Quebec-only Fête nationale.
 *
 * Same-day pickup is deliberately not offered. Canada Post accepts it only
 * before a per-postal-code cutoff and only an hour or more ahead of the ready
 * time, which made the strip's contents depend on what time of day the
 * coordinator happened to open the modal. Booking from tomorrow onwards is the
 * rule we actually work to, and it removes the cutoff from the flow entirely.
 */
export const nextBusinessDays = (count: number, province?: string | null): string[] => {
  const dates: string[] = [];
  const cursor = new Date();

  cursor.setDate(cursor.getDate() + 1);
  while (dates.length < count) {
    const date = toDateStr(cursor);
    if (cursor.getDay() !== 0 && cursor.getDay() !== 6 && !canadaPostHoliday(date, province)) {
      dates.push(date);
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

/** "Tue, Aug 25", with "(Today)" appended when it is. */
export const formatDateLabel = (dateStr: string): string => {
  const date = parseDateStr(dateStr);
  const label = `${DAY_NAMES[date.getDay()]}, ${MONTH_NAMES[date.getMonth()]} ${date.getDate()}`;
  return dateStr === todayStr() ? `${label} (Today)` : label;
};

/** "Tuesday, August 25" — the banner's phrasing, where there's room for the full day. */
export const formatPickupDay = (dateStr: string): string => {
  const date = parseDateStr(dateStr);
  const day = date.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' });
  if (dateStr === todayStr()) return `${day} (today)`;
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dateStr === toDateStr(tomorrow)) return `${day} (tomorrow)`;
  return day;
};

/** HH:MM options on the quarter hour, inclusive of both bounds. */
export const timeOptions = (startHour: number, endHour: number): string[] => {
  const options: string[] = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    for (const minute of [0, 15, 30, 45]) {
      if (hour === endHour && minute > 0) break;
      options.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    }
  }
  return options;
};

export const minutesOf = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};


/** "13:00" → "1:00 PM". */
export const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(minutes || 0).padStart(2, '0')} ${suffix}`;
};
